import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENV_NAME = import.meta.env.VITE_ENV_NAME || 'DEVELOPMENT';

/**
 * Initialize Sentry error monitoring.
 * Only initializes if VITE_SENTRY_DSN is set in the environment.
 *
 * Setup instructions:
 * 1. Create a free account at https://sentry.io
 * 2. Create a new React project
 * 3. Copy the DSN and add to .env.production and .env.development:
 *    VITE_SENTRY_DSN=https://your-dsn@sentry.io/project-id
 * 4. For source map uploads, set SENTRY_AUTH_TOKEN and SENTRY_ORG in CI/CD
 */
export function initSentry(): void {
  if (!SENTRY_DSN) {
    if (ENV_NAME === 'DEVELOPMENT') {
      console.log('Sentry: No DSN configured, skipping initialization (set VITE_SENTRY_DSN to enable)');
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENV_NAME.toLowerCase(),

    // Set sample rates
    tracesSampleRate: ENV_NAME === 'PRODUCTION' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: ENV_NAME === 'PRODUCTION' ? 1.0 : 0,

    // Only send errors in production, or all in development
    beforeSend(event) {
      // Filter out known non-actionable errors
      if (event.exception?.values?.[0]?.value?.includes('ResizeObserver loop')) {
        return null;
      }
      return event;
    },

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
  });
}

/**
 * Set the current user context in Sentry for error attribution.
 */
export function setSentryUser(user: { id: string; email?: string; role?: string } | null): void {
  if (!SENTRY_DSN) return;

  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      role: user.role,
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Capture an error manually with optional context.
 */
export function captureError(error: Error, context?: Record<string, string>): void {
  if (!SENTRY_DSN) {
    console.error('Uncaptured error (Sentry not configured):', error);
    return;
  }

  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

/**
 * Capture a non-fatal warning message (not an exception) with optional context.
 */
export function captureWarning(message: string, context?: Record<string, string>): void {
  if (!SENTRY_DSN) {
    console.warn('Uncaptured warning (Sentry not configured):', message, context ?? '');
    return;
  }

  Sentry.withScope((scope) => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }
    Sentry.captureMessage(message, 'warning');
  });
}

export { Sentry };
